<script>
    import Fa from 'svelte-fa';
    import { faBarsStaggered, faCartShopping, faUser, faReceipt } from '@fortawesome/pro-regular-svg-icons';

    import { subscribe } from '$lib/utils/pubsub';
    import { cartUpdate } from '$lib/utils/shopify/cart/events';

    import ThemeSwitcher from '$lib/components/ThemeSwitcher.svelte';

    export let drawerMenuOpen;
    export let drawerCartOpen;
    export let featuredMenu;

    let searchModalOpen = false;
    let cartCount = 0;

    // pubsub cartData
    subscribe(cartUpdate, (message) => {
        cartCount = message?.cart?.totalQuantity;
    });

    // scroll header peekaboo
    let headerClass = '';
    let scrollY = 0;
    let lastY = 0;

    function getScrollState(currentY) {
        const deltaY = lastY - currentY;
        lastY = currentY;
        const toleranceThreshold = 30;

        // Check if the user is near the top of the page. (safe zone)
        const isAtTop = currentY < 100;

        // Check if the change in Y position (deltaY) is within the specified tolerance range.
        // If it is, maintain the current header class to prevent frequent updates.
        if (Math.abs(deltaY) <= toleranceThreshold && !isAtTop) {
            return headerClass;
        }

        // If deltaY is negative and not in safe zone, it indicates the user is scrolling down.
        // Hide the header with '-translate-y-full'.
        if (deltaY < 0 && !isAtTop) {
            return 'xs:opacity-30 lg:-translate-y-full lg:opacity-100';
        }

        // If deltaY is not within the tolerance range and not negative,
        // it indicates the user is scrolling up or at the top.
        // Show the header with 'translate-y-0'.
        return 'xs:opacity-100 lg:translate-y-0 lg:opacity-100';
    }

    $: headerClass = getScrollState(scrollY);

    // Set haptic feedback length for btn if available
    const hapticVibrate = () => {
        if (navigator && 'vibrate' in navigator) {
            navigator.vibrate(1);
            return;
        }
        return false;
    };
</script>

<svelte:window bind:scrollY />

<header
    class="lg:top:0 fixed bottom-0 left-0 right-0 z-30 bg-color-header pb-3 pt-3 text-white shadow-lg drop-shadow-lg transition-all duration-300 ease-in-out md:bg-black lg:bottom-auto lg:py-1.5 {headerClass}"
    class:top-0={searchModalOpen}
    class:!opacity-100={searchModalOpen}
>
    <div
        class={`container m-auto flex w-full items-center justify-around transition-all duration-200 ease-in-out lg:justify-normal lg:px-4
        ${scrollY > 75 ? 'lg:py-0' : 'lg:py-2.5'}`}
    >
        <button
            class="relative flex h-12 w-12 flex-col items-center justify-center text-lg text-color-text md:text-white lg:text-xl"
            on:click={() => {
                hapticVibrate();
                drawerMenuOpen = !drawerMenuOpen;
            }}
            aria-label="Open Menu Drawer"
        >
            <Fa icon={faBarsStaggered} class="block lg:text-xl" />
            <span class="block text-xs lg:hidden">Menu</span>
        </button>

        <a href="/" aria-label="Go to Home Page" title="Home Page" class="hidden border-r border-white/20 pr-7 lg:block tabProPort:border-0 tabRetina:border-0">
            <img src="/svg/CaktusLogo.svg" alt="Caktus Logo" width="300" height="58" class="ml-4 w-32" fetchpriority="high" />
        </a>

        <div class="hidden items-center justify-start lg:flex lg:grow">
            {#each featuredMenu?.menuItems as item (item)}
                <a href={item?.url} title={item?.title} class="ml-7 flex items-center py-1 tabProPort:hidden tabRetina:hidden">
                    {#if item?.image?.url}<img
                            src={item?.image?.url}
                            alt={`Icon for ${item?.title}`}
                            width="150"
                            height="150"
                            class="mr-2 inline-block w-5"
                        />{/if}
                    <span class="text-base font-semibold capitalize tracking-normal text-white">{item?.title}</span>
                </a>
            {/each}
        </div>

        <a
            href="/"
            class="relative flex h-12 w-12 flex-col items-center justify-center text-lg lg:text-xl"
            aria-label="Caktus Vape Home"
            on:click={() => hapticVibrate()}
        >
            <img src="/svg/CaktusEmblem.svg" alt="Caktus Logo" width="150" height="150" fetchpriority="high" class="block w-[1.28rem] dark:invert lg:hidden" />
            <span class="block text-xs text-color-text lg:hidden">Browse</span>
        </a>

        <div class="hidden lg:block">
            <ThemeSwitcher classes="relative flex h-12 w-12 items-center justify-center text-xl" />
        </div>

        <a
            data-sveltekit-preload-code="eager"
            class="relative flex h-12 w-12 flex-col items-center justify-center text-lg text-color-text md:text-white lg:hidden lg:text-xl"
            href="/account/orders"
            aria-label="View your Orders"
            on:click={() => hapticVibrate()}
        >
            <Fa icon={faReceipt} class="block" />
            <span class="block text-xs lg:hidden">Orders</span>
        </a>

        <a
            class="relative hidden h-12 w-12 flex-col items-center justify-center text-lg text-color-text md:text-white lg:flex lg:text-xl"
            href="/account"
            aria-label="View your Orders"
        >
            <Fa icon={faUser} class="block" />
            <span class="block text-xs lg:hidden">Account</span>
        </a>

        <button
            class=" relative flex h-12 w-12 flex-col items-center justify-center text-lg text-color-text md:text-white lg:text-xl"
            on:click={() => {
                hapticVibrate();
                drawerCartOpen = !drawerCartOpen;
            }}
            aria-label="Open Cart Drawer"
        >
            {#if cartCount > 0}
                <span
                    class="absolute -top-2 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-xs text-white lg:right-0 lg:top-0 lg:h-5 lg:w-5"
                >
                    {cartCount}
                </span>
            {/if}
            <Fa icon={faCartShopping} class="block" />
            <span class="block text-xs lg:hidden">Cart</span>
        </button>
    </div>
</header>
